import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SiWordpress, SiGithub, SiGoogleanalytics, SiCloudflare, SiSalesforce, SiSemrush } from "react-icons/si";
import { twMerge } from "tailwind-merge";
import ModernButton from './ModernButton';

export const FoldingLogos = () => {
  return (
    <section className="w-full bg-darkBlue relative overflow-hidden">
      <div className="mx-auto w-full max-w-7xl px-6 py-16 md:py-24 lg:py-28">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12">
          <Copy />
          <LogoRolodex
            items={[
            <LogoItem key={1} className="bg-blue-600 text-white">
              <SiWordpress />
            </LogoItem>,
            <LogoItem key={2} className="bg-orange-500 text-white">
              <SiGoogleanalytics />
            </LogoItem>,
            <LogoItem key={3} className="bg-gray-900 text-white">
              <SiGithub />
            </LogoItem>,
            <LogoItem key={4} className="bg-orange-400 text-white">
              <SiCloudflare />
            </LogoItem>,
            <LogoItem key={5} className="bg-blue-500 text-white">
              <SiSalesforce />
            </LogoItem>,
            <LogoItem key={6} className="bg-orange-600 text-white">
              <SiSemrush />
            </LogoItem>,
            ]}
          />
        </div>
      </div>
    </section>
  );
};

const Copy = () => (
  <div className="flex-1 max-w-3xl">
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
      Power Up Your Online Presence
    </h2>
    <p className="text-base md:text-lg text-neutral-300 mb-8 leading-relaxed">
      Need a new website, assistance with Salesforce integration, SEO optimization, or help implementing server-side tagging? I'm here to make it happen. From seamless digital transformations to data-driven, search-friendly solutions, I'm your partner in creating powerful, customized web experiences.
    </p>
    <ModernButton text="Contact now" link="/contact" />
  </div>
);

const DELAY_IN_MS = 2500;
const TRANSITION_DURATION_IN_SECS = 1.5;

const LogoRolodex = ({ items }) => {
  const intervalRef = useRef(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIndex((pv) => pv + 1);
    }, DELAY_IN_MS);

    return () => {
      clearInterval(intervalRef.current || undefined);
    };
  }, []);

  return (
    <div
      style={{
        transform: "rotateY(-20deg)",
        transformStyle: "preserve-3d",
      }}
      className="relative z-0 h-52 w-72 shrink-0 rounded-xl border border-neutral-700 bg-neutral-800"
    >
      <AnimatePresence mode="sync">
        <motion.div
          style={{
            y: "-50%",
            x: "-50%",
            clipPath: "polygon(0 0, 100% 0, 100% 50%, 0 50%)",
            zIndex: -index,
            backfaceVisibility: "hidden",
          }}
          key={index}
          transition={{
            duration: TRANSITION_DURATION_IN_SECS,
            ease: "easeInOut",
          }}
          initial={{ rotateX: "0deg" }}
          animate={{ rotateX: "0deg" }}
          exit={{ rotateX: "-180deg" }}
          className="absolute left-1/2 top-1/2"
        >
          {items[index % items.length]}
        </motion.div>
        <motion.div
          style={{
            y: "-50%",
            x: "-50%",
            clipPath: "polygon(0 50%, 100% 50%, 100% 100%, 0 100%)",
            zIndex: index,
            backfaceVisibility: "hidden",
          }}
          key={(index + 1) * 2}
          initial={{ rotateX: "180deg" }}
          animate={{ rotateX: "0deg" }}
          exit={{ rotateX: "0deg" }}
          transition={{
            duration: TRANSITION_DURATION_IN_SECS,
            ease: "easeInOut",
          }}
          className="absolute left-1/2 top-1/2"
        >
          {items[index % items.length]}
        </motion.div>
      </AnimatePresence>

      <hr
        style={{
          transform: "translateZ(1px)",
        }}
        className="absolute left-0 right-0 top-1/2 z-[999999999] -translate-y-1/2 border-t-2 border-neutral-800"
      />
    </div>
  );
};

const LogoItem = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        "grid h-44 w-64 place-content-center rounded-lg bg-neutral-700 text-7xl text-neutral-50",
        className
      )}
    >
      {children}
    </div>
  );
};